import { useMemo, useState } from "react";
import { Box, styled } from "@mui/material";
import { Network, Check } from "lucide-react";
import { FieldCard } from "@components/form/FieldCard";
import { FieldInput } from "@components/form/FieldInput";
import { FormFooter } from "@components/form/FormFooter";
import { PickerSheet, PickerOption } from "@components/form/PickerSheet";
import { tokens } from "@styles/tokens";
import { useLocationForm } from "../../../hooks/useLocationForm";

interface LocationFormProps {
  handleClose: () => void;
  createFromStockView?: boolean;
}

const ROOT_OPTION = "";

/**
 * Create/Edit location drawer on the shared forms pattern: a name field and a
 * parent picker (with a "top level" option). Logic lives in useLocationForm.
 */
export const LocationForm = ({
  handleClose,
  createFromStockView = false,
}: LocationFormProps) => {
  const {
    isEdit,
    name,
    parentId,
    parentOptions,
    error,
    submitting,
    setName,
    setParent,
    submit,
  } = useLocationForm(createFromStockView);

  const [pickerOpen, setPickerOpen] = useState(false);

  const options = useMemo<PickerOption[]>(
    () => [
      { value: ROOT_OPTION, label: "Top level (no parent)" },
      ...parentOptions.map((o) => ({ value: o.value, label: o.label })),
    ],
    [parentOptions]
  );
  const parentLabel =
    options.find((o) => o.value === parentId)?.label ?? "Top level (no parent)";

  return (
    <Container>
      <Scroll>
        <FieldCard label="Name" error={error}>
          <FieldInput
            value={name}
            placeholder="Location name"
            onChange={(e) => setName(e.target.value)}
            aria-label="Location name"
          />
        </FieldCard>

        <FieldCard
          label="Parent Location"
          icon={<Network size={18} />}
          onClick={() => setPickerOpen(true)}
        >
          <ParentName>{parentLabel}</ParentName>
        </FieldCard>
      </Scroll>

      <FormFooter
        onCancel={handleClose}
        onSubmit={submit}
        submitting={submitting}
        submitLabel={isEdit ? "Save Changes" : "Create Location"}
        submitIcon={<Check size={18} />}
      />

      <PickerSheet
        open={pickerOpen}
        title="Parent Location"
        searchPlaceholder="Search locations…"
        options={options}
        selectedValue={parentId}
        onSelect={setParent}
        onClose={() => setPickerOpen(false)}
      />
    </Container>
  );
};

const Container = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
});

const Scroll = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(4),
  padding: tokens.space(4),
  backgroundColor: theme.skuey.surfaceAlt,
  "& > *": { flexShrink: 0 },
}));

const ParentName = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.body,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
